import { DeviceType } from '@/__generated__/graphql';
import { z } from 'zod';
import {
  getTypeOption,
  isIntegrationType,
  isNonIntegrationType,
} from './components/form/deviceTypes';

export const CreateDeviceSchema = z
  .object({
    name: z.string().trim(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    type: z.string(),
    shares: z.number().nullish(),
    irrigationId: z.number(),
    organizationId: z.number(),
    isIntegration: z.boolean(),
    integrationId: z.number().nullish(),
    children: z
      .array(
        z.object({
          sensorName: z.string(),
          sensorVersion: z.string().optional(),
          sensorsMappers: z.array(
            z.object({
              typeId: z.number(),
              telemetrySensorId: z.number().optional(),
            }),
          ),
        }),
      )
      .optional(),

    // postprocess fields
    typeEnum: z.nativeEnum(DeviceType).nullish(),
    sensorVersion: z.string().trim().nullish(),
    location: z
      .object({
        latitude: z.number(),
        longitude: z.number(),
      })
      .nullish(),
  })
  .superRefine((data, ctx) => {
    if (data.isIntegration && typeof data.integrationId !== 'number') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'integrationId is required when isIntegration is true',
        path: ['integrationId'],
      });
    }

    if (data.isIntegration && !isIntegrationType(data.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Type must be a valid integration type when isIntegration is true',
        path: ['type'],
      });
    }

    if (!data.isIntegration && !isNonIntegrationType(data.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Type must be a valid non-integration type when isIntegration is false',
        path: ['type'],
      });
    }
  })
  .transform((data, ctx) => {
    const typeOption = getTypeOption(data.type);

    if (!typeOption) {
      ctx.addIssue({
        code: 'custom',
        message: 'Unexpected type option selected',
        path: ['type'],
      });
      return z.NEVER;
    }

    const location =
      data.latitude != null && data.longitude != null
        ? {
            latitude: data.latitude,
            longitude: data.longitude,
          }
        : null;

    return {
      ...data,
      location,
      typeEnum: typeOption.enumValue,
      sensorVersion: typeOption.sensorVersion,
      integrationId: data.isIntegration ? data.integrationId : null,
    };
  });

export type FormValues = z.infer<typeof CreateDeviceSchema>;
